import { motion, AnimatePresence, useIsPresent } from 'motion/react';
import type { ReactNode } from 'react';

/** Full-height themed stage with game-show texture. `game` selects the palette via data-game. */
export function Stage({ game, children, className = '' }: { game?: string; children: ReactNode; className?: string }) {
  return (
    <div data-game={game} className={`stage-bg dots relative min-h-dvh flex flex-col ${className}`}>
      {children}
    </div>
  );
}

/** Animated swap between phase screens keyed by phase+round. */
export function PhaseSwap({ id, children, reduced }: { id: string; children: ReactNode; reduced?: boolean }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={id}
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, y: -16, scale: 0.98 }}
        transition={{ duration: reduced ? 0.1 : 0.28, ease: [0.2, 0.8, 0.2, 1] }}
        className="flex-1 flex flex-col"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Big banner used for round intros and reveals. */
export function Banner({ kicker, title, sub, big }: { kicker?: string; title: string; sub?: string; big?: boolean }) {
  return (
    <div className="text-center flex flex-col items-center gap-2">
      {kicker && <span className="chip uppercase tracking-widest text-[var(--accent-2)]">{kicker}</span>}
      <motion.h1
        initial={{ scale: 0.7, rotate: -3, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 16 }}
        className={`font-display font-black leading-[0.95] text-balance ${big ? 'text-6xl md:text-8xl' : 'text-4xl'}`}
        style={{ textShadow: '0 4px 0 color-mix(in oklab, var(--accent) 70%, black)' }}
      >
        {title}
      </motion.h1>
      {sub && <p className={`text-mute text-balance ${big ? 'text-2xl' : 'text-base'}`}>{sub}</p>}
    </div>
  );
}

/** Confetti burst made of DOM pieces (cheap, no canvas). */
export function Confetti({ count = 40, reduced }: { count?: number; reduced?: boolean }) {
  if (reduced) return null;
  const colors = ['var(--accent)', 'var(--accent-2)', '#ffffff', '#3ddc97', '#ff7ab8'];
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-50" aria-hidden>
      {Array.from({ length: count }, (_, i) => {
        const left = (i * 37) % 100;
        const delay = (i % 10) * 0.05;
        const rot = (i * 47) % 360;
        return (
          <motion.span
            key={i}
            className="absolute top-0 block w-2.5 h-4 rounded-sm"
            style={{ left: `${left}%`, background: colors[i % colors.length] }}
            initial={{ y: -40, rotate: rot, opacity: 1 }}
            animate={{ y: '110vh', rotate: rot + 540, opacity: [1, 1, 0.6] }}
            transition={{ duration: 2.2 + (i % 5) * 0.25, delay, ease: 'easeIn' }}
          />
        );
      })}
    </div>
  );
}

export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  return (
    <AnimatePresence>
      {open && (
        <SheetLayer>
          <button className="absolute inset-0 bg-black/60" aria-label="Close" onClick={onClose} />
          {/* Header sits outside the scrolling body so Done never scrolls away; the sheet fades rather than
              slides, so nothing moves under a finger that is already tapping. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="relative card w-full sm:max-w-lg max-h-[88dvh] flex flex-col overflow-hidden safe-bottom rounded-b-none sm:rounded-b-3xl"
            // Solid, unlike other cards (92%), so the page behind never shows through the sheet's text.
            style={{ background: 'var(--color-ink-2)' }}
          >
            <div className="shrink-0 px-5 pt-5 pb-3 flex items-center justify-between">
              <h2 className="font-display font-black text-2xl">{title}</h2>
              <button className="btn btn-sm btn-secondary" onClick={onClose}>Done</button>
            </div>
            <div className="overflow-y-auto overscroll-contain px-5 pb-5">{children}</div>
          </div>
        </SheetLayer>
      )}
    </AnimatePresence>
  );
}

/**
 * The sheet's full-screen layer. While it fades out after closing it no longer takes taps and is hidden
 * from screen readers, so a sheet opened right after another (Settings, then TV) is never blocked or
 * shadowed by the old one.
 */
function SheetLayer({ children }: { children: ReactNode }) {
  const present = useIsPresent();
  return (
    <motion.div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center"
      style={{ pointerEvents: present ? 'auto' : 'none' }}
      // A closing sheet is out of the accessibility tree and unfocusable straight away.
      aria-hidden={present ? undefined : true}
      inert={!present}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {children}
    </motion.div>
  );
}
